import * as THREE from 'three';
import ClipperLib from 'clipper-lib';
import { dividerArray } from '../configurator/dividerArray';
import { SWING_SETS, SWING_CAD, swingSection, swingDimensions, assembleSwing, addSwingThreshold } from './swingSections';
import { archGrillePaths } from '../configurator/archGrilles';

// Frame depth changes with the selected set; both use the SAME 70mm moving leaf.
export { SWING_SETS } from './swingSections';
export const SWING_PARTS = { 'hd88-swing': SWING_SETS.single };

function addHinges(body, height, frame) {
  [.22, height / 2, height - .22].forEach(y => {
    const hinge = new THREE.Mesh(new THREE.CylinderGeometry(.009, .009, .1, 12), frame);
    hinge.position.set(-.012, y, .045); hinge.name = 'swing-hinge'; body.add(hinge);
  });
}

function addLever(body, width, frame) {
  const rose = new THREE.Mesh(new THREE.CylinderGeometry(.022, .022, .012, 16), frame);
  rose.rotation.x = Math.PI / 2; rose.position.set(width - .06, 1.0, .05);
  const lever = new THREE.Mesh(new THREE.BoxGeometry(.13, .018, .018), frame);
  lever.position.set(width - .11, 1.0, .07); lever.castShadow = true; lever.name = 'swing-handle';
  body.add(rose, lever);
}

export function makeSwingDoor(product, materials, options = {}) {
  if (options.top === 'arch') return makeArchSwingDoor(product, materials, options);
  const dimensions = swingDimensions(options), { parts, leafInset, bottom, leafZ, depth } = dimensions;
  const width = options.width ?? product.width, height = options.height ?? product.height;
  const leaves = options.leaves === 'double' ? 2 : 1, hand = options.hand || 'left', outward = options.direction === 'outward';
  const group = new THREE.Group(), frame = options.frame || materials.makeFrame();
  const materialByRole = { frame, thermal: materials.thermal, gasket: materials.gasket };
  addSwingThreshold(group, dimensions, width, height, materialByRole);
  assembleSwing(group, [[parts.frame, ['head', 'left', 'right']]], width, height, materialByRole);
  const clear = width - 2 * leafInset, lw = (clear - (leaves === 2 ? SWING_CAD.centerGap : 0)) / leaves, lh = height - leafInset - bottom;
  const hinges = leaves === 2 ? [-1, 1] : [hand === 'left' ? -1 : 1];
  const pivots = hinges.map(side => {
    const pivot = makeSwingLeaf(lw, lh, materialByRole, materials.glass, options.dividers, parts.leaf);
    addHinges(pivot, lh, frame);
    addLever(pivot, lw, frame);
    if (leaves === 2 && side < 0) addFalseMullion(pivot, lw, lh, parts.mullion, materialByRole);
    pivot.position.set(side * clear / 2, bottom, leafZ);
    if (side > 0) pivot.scale.x = -1;
    group.add(pivot);
    return { pivot, side };
  });
  const animate = fraction => {
    const angle = Math.min(1, Math.max(0, fraction)) * THREE.MathUtils.degToRad(90) * (outward ? -1 : 1);
    pivots.forEach(({ pivot, side }) => { pivot.rotation.y = side < 0 ? -angle : angle; });
    group.userData.panelPositions = pivots.map(({ pivot }) => ({ x: +pivot.position.x.toFixed(4), z: +pivot.position.z.toFixed(4) }));
    group.userData.swingAngle = +THREE.MathUtils.radToDeg(Math.abs(angle)).toFixed(1);
  };
  animate(options.fraction || 0);
  Object.assign(group.userData, { swing: true, threshold: options.threshold === 'high' ? 'high' : 'low', sillCode: dimensions.rise ? parts.highSill : parts.sill, leaves, hand, direction: outward ? 'outward' : 'inward', animate, pickTargets: group.children, ...swingMetrics(dimensions, pivots) });
  group.traverse(o => { if (o.isMesh) o.userData.productId = product.id; });
  return { group, frame, animate, progress: 0, raised: 0, panelWidth: lw, panelHeight: lh, count: leaves, depth };
}

function makeSwingLeaf(width, height, roles, glass, dividers, code) {
  const pivot = new THREE.Group(), body = new THREE.Group();
  const inset = swingSection(code).height_mm / 1000 - SWING_CAD.glazingBite;
  assembleSwing(body, [[code, ['sill', 'head', 'left', 'right']]], width, height, roles);
  const pane = new THREE.Mesh(new THREE.BoxGeometry(width - 2 * inset, height - 2 * inset, .028), glass);
  pane.position.set(0, height / 2, SWING_CAD.glassZ); pane.name = 'swing-glass'; pane.receiveShadow = true; body.add(pane);
  if (dividers) dividerArray(body, 1, width, height, dividers, roles.frame, { glassWidth: width - 2 * inset, glassHeight: height - 2 * inset, glassCenterY: height / 2 })([{ x: 0, z: SWING_CAD.glassZ }]);
  body.position.x = width / 2; pivot.add(body); pivot.userData.body = body; return pivot;
}

function addFalseMullion(pivot, width, height, code, roles) {
  const mullion = new THREE.Group(); mullion.name = 'swing-false-mullion';
  assembleSwing(mullion, [[code, ['left']]], 0, height, roles);
  mullion.position.x = width - (swingSection(code).height_mm / 1000 - SWING_CAD.centerGap) / 2;
  pivot.add(mullion);
}

function swingMetrics(dimensions, pivots) {
  const { parts, depth, face, leafInset, leafFace } = dimensions, bounds = new THREE.Box3();
  // Measure source-profile mesh geometry, excluding projecting handle/hinge hardware.
  pivots[0].pivot.traverse(o => { if (o.isMesh && o.userData.profileCode === parts.leaf) { o.geometry.computeBoundingBox(); bounds.union(o.geometry.boundingBox); } });
  return { frameCode: parts.frame, leafCode: parts.leaf, frameDepthMm: depth * 1000,
    leafDepthMm: (bounds.max.z - bounds.min.z) * 1000, frameFaceMm: face * 1000,
    sideSightlineMm: (leafInset + leafFace) * 1000, leafPivots: pivots.map(p => p.pivot) };
}

export function makeSwingSpecimen(product, type, frame, materials) {
  const dimensions = swingDimensions({}), { parts, leafInset, bottom, leafZ } = dimensions, group = new THREE.Group(), length = .38;
  const materialByRole = { frame, thermal: materials.thermal, gasket: materials.gasket };
  const base = new THREE.Group(), leaf = new THREE.Group();
  assembleSwing(base, [[type === 'sill' ? parts.sill : parts.frame, ['sill']]], length, .2, materialByRole);
  assembleSwing(leaf, [[parts.leaf, ['sill']]], length - .04, .2, materialByRole);
  leaf.position.set(0, type === 'head' ? leafInset : bottom, leafZ);
  group.add(base, leaf);
  group.userData.profileCode = type === 'sill' ? parts.sill : parts.frame;
  group.userData.profileDepth = dimensions.depth;
  group.traverse(o => { if (o.isMesh) o.userData.productId = product.id; });
  return group;
}

const unit = ([x, y]) => { const l = Math.hypot(x, y) || 1; return [x / l, y / l]; };
// Sweep a CAD section (depth, inset) along an outline; open paths are used for frame jambs + arch.
function sweep(rings, path, depth, closed) {
  const n = path.length, normal = (a, b) => unit([-(b[1] - a[1]), b[0] - a[0]]);
  const shifts = path.map((p, i) => {
    const prev = path[(i + n - 1) % n], next = path[(i + 1) % n];
    if (!closed && i === 0) return normal(p, next);
    if (!closed && i === n - 1) return normal(prev, p);
    const a = normal(prev, p), b = normal(p, next), d = 1 + a[0] * b[0] + a[1] * b[1];
    return [(a[0] + b[0]) / d, (a[1] + b[1]) / d];
  });
  const positions = [], indices = [];
  rings.forEach(ring => {
    const start = positions.length / 3, m = ring.length;
    path.forEach(([x, y], i) => ring.forEach(([z, inset]) => positions.push(x + shifts[i][0] * inset / 1000, y + shifts[i][1] * inset / 1000, z / 1000 - depth / 2)));
    for (let i = 0; i < (closed ? n : n - 1); i++) for (let j = 0; j < m; j++) {
      const a = start + i * m + j, b = start + ((i + 1) % n) * m + j, c = start + ((i + 1) % n) * m + (j + 1) % m, d = start + i * m + (j + 1) % m;
      indices.push(a, b, c, a, c, d);
    }
  });
  const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); g.setIndex(indices); g.computeVertexNormals();
  return g;
}

function sweepProfile(parent, code, path, roles, closed) {
  const data = swingSection(code);
  data.components.forEach(c => c.shapes.forEach(s => {
    const material = roles[c.role] || roles.frame; material.side = THREE.DoubleSide;
    const mesh = new THREE.Mesh(sweep([s.outer, ...(s.holes || [])], path, data.depth_mm / 1000, closed), material);
    mesh.castShadow = true; mesh.receiveShadow = true;
    Object.assign(mesh.userData, { profileCode: code, profileRole: c.role, profileDepthMm: data.depth_mm }); parent.add(mesh);
  }));
}

// Round-top (half-circle) outline: rectangle of height `spring` with a semicircle of radius width/2.
export const archPath = (width, height, segments = 40) => {
  const r = width / 2, spring = height - r;
  return [[r, 0], ...Array.from({ length: segments + 1 }, (_, i) => { const a = i * Math.PI / segments; return [r * Math.cos(a), spring + r * Math.sin(a)]; }), [-r, 0]];
};

function leafOutline(x0, x1, bottom, width, height, face) {
  const r = width / 2 - face, spring = height - width / 2, pts = [[x0, bottom], [x1, bottom]];
  const yAt = x => spring + Math.sqrt(Math.max(0, r * r - x * x));
  const steps = 20;
  for (let i = 0; i <= steps; i++) { const x = x1 - (x1 - x0) * i / steps; pts.push([x, yAt(x)]); }
  return pts;
}

export function makeArchSwingDoor(product, materials, options = {}) {
  const dimensions = swingDimensions(options), { parts: set, leafInset, leafFace, bottom, leafZ, depth } = dimensions;
  const width = options.width ?? product.width, height = options.height ?? product.height;
  const leaves = options.leaves === 'double' ? 2 : 1, hand = options.hand || 'left', outward = options.direction === 'outward';
  const group = new THREE.Group(), frame = options.frame || materials.makeFrame(), roles = { frame, thermal: materials.thermal, gasket: materials.gasket };
  const clear = width - 2 * leafInset, gap = SWING_CAD.centerGap;
  sweepProfile(group, set.frame, archPath(width, height), roles, false);
  addSwingThreshold(group, dimensions, width, height, roles);
  const spans = leaves === 2 ? [[-clear / 2, -gap / 2, -1], [gap / 2, clear / 2, 1]] : [[-clear / 2, clear / 2, hand === 'left' ? -1 : 1]];
  const pivots = spans.map(([x0, x1, side]) => {
    const outline = leafOutline(x0, x1, bottom, width, height, leafInset);
    const hingeX = side < 0 ? x0 : x1, pivot = new THREE.Group(), body = new THREE.Group();
    const local = outline.map(([x, y]) => [x - hingeX, y - bottom]);
    sweepProfile(body, set.leaf, local, roles, true);
    const inner = insetPolygon(local, leafFace - SWING_CAD.glazingBite);
    if (inner.length > 2) {
      const pane = new THREE.Mesh(new THREE.ExtrudeGeometry(new THREE.Shape(inner.map(([x, y]) => new THREE.Vector2(x, y))), { depth: .028, bevelEnabled: false, steps: 1 }), materials.glass);
      pane.position.z = SWING_CAD.glassZ - .014; pane.name = 'swing-arch-glass'; body.add(pane);
      addArchGrilles(body, inner, options.dividers, frame, [-hingeX, height - width / 2 - bottom]);
    }
    pivot.add(body); addHinges(pivot, Math.max(.6, height - width / 2 - bottom), frame);
    const leafW = x1 - x0, lever = new THREE.Group(); addLever(lever, leafW, frame);
    if (side > 0) lever.scale.x = -1;
    pivot.add(lever);
    if (leaves === 2 && side < 0) addFalseMullion(pivot, leafW, Math.max(...local.map(p => p[1])), set.mullion, roles);
    pivot.userData.body = body;
    pivot.position.set(hingeX, bottom, leafZ); group.add(pivot);
    return { pivot, side };
  });
  const animate = fraction => {
    const angle = Math.min(1, Math.max(0, fraction)) * THREE.MathUtils.degToRad(90) * (outward ? -1 : 1);
    pivots.forEach(({ pivot, side }) => { pivot.rotation.y = side < 0 ? -angle : angle; });
    group.userData.panelPositions = pivots.map(({ pivot }) => ({ x: +pivot.position.x.toFixed(4), z: +pivot.position.z.toFixed(4) }));
    group.userData.swingAngle = +THREE.MathUtils.radToDeg(Math.abs(angle)).toFixed(1);
  };
  animate(options.fraction || 0);
  Object.assign(group.userData, { swing: true, arch: 'round', threshold: options.threshold === 'high' ? 'high' : 'low', sillCode: dimensions.rise ? set.highSill : set.sill, leaves, hand, direction: outward ? 'outward' : 'inward', animate, pickTargets: group.children, ...swingMetrics(dimensions, pivots) });
  group.traverse(o => { if (o.isMesh) o.userData.productId = product.id; });
  return { group, frame, animate, progress: 0, raised: 0, panelWidth: clear / leaves, panelHeight: height - dimensions.sill, count: leaves, depth };
}

function insetPolygon(points, distance) {
  const S = 1e6, offset = new ClipperLib.ClipperOffset(10, 10), result = [];
  offset.AddPath(points.map(([x, y]) => ({ X: Math.round(x * S), Y: Math.round(y * S) })), ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);
  offset.Execute(result, -distance * S);
  return result.length ? result[0].map(p => [p.X / S, p.Y / S]) : [];
}

function addArchGrilles(body, glass, dividers, frame, center) {
  const paths = archGrillePaths(glass, dividers, center); if (!paths.length) return;
  const shapes = paths.map(path => new THREE.Shape(path.map(([x, y]) => new THREE.Vector2(x, y))));
  const bars = new THREE.Mesh(new THREE.ExtrudeGeometry(shapes, { depth: .034, bevelEnabled: false, steps: 1 }), frame);
  bars.position.z = SWING_CAD.glassZ - .017; bars.castShadow = true; bars.name = 'swing-arch-grilles';
  Object.assign(bars.userData, { dividerBars: true, dividerCount: Number(dividers.horizontal) + Number(dividers.vertical) + (dividers.pattern === 'sunburst' ? Number(dividers.rays) : 0) });
  body.add(bars);
}
